import { useEffect, useState, type ReactNode } from 'react';

import { ThumbnailImage } from '@/shared/ui/ThumbnailImage';

import type { ThumbnailRenderable } from '../AssetThumbnail';

/**
 * A file at full size: the same ladder `AssetThumbnail` walks, filling whatever
 * box it is handed instead of a cell.
 *
 * image → the file itself; video and audio → a player; anything else → the
 * server-generated poster (the first page of a PDF, a frame of a video) when
 * extraction produced one. The last rung is the caller's `fallback`, and it
 * means what it says: there is nothing to show here, look at the file where it
 * lives.
 *
 * Deliberately not a viewer. Paging a PDF, annotating one, or reading a text
 * body are the asset page's job, and a second implementation of them here is a
 * second one to keep in step. The shape is `ThumbnailRenderable` rather than a
 * `ProjectFile` for the same reason that one is: a caller holding a file-like
 * view can satisfy it without inventing the fields it does not have.
 */
export interface FilePreviewProps {
  file: ThumbnailRenderable;
  /** Takes the frame when the file has nothing to render. */
  fallback: ReactNode;
  /** Sizes the box the preview fills; defaults to filling its parent. */
  className?: string;
}

export function FilePreview({ file, fallback, className }: FilePreviewProps) {
  const { assetType, fileType, fileName } = file;
  const downloadUrl = file.downloadUrl as string | null | undefined;
  const thumbnailUrl = file.thumbnailUrl as string | null | undefined;
  const frameClass = className ?? 'h-full w-full';

  // A file this browser cannot decode fires `error` and never fires anything
  // else, so that event is the only notice that a black rectangle or a dead
  // control bar is all these elements will ever be. Saying so drops the file
  // down the ladder to the rungs below, which is the poster for a video and the
  // caller's fallback for audio - the same places a file with no player at all
  // lands. Scoped to the URL, because a re-signed one deserves a real attempt.
  const [undecodable, setUndecodable] = useState(false);
  useEffect(() => setUndecodable(false), [downloadUrl]);

  if (assetType === 'image' && downloadUrl) {
    return (
      <ThumbnailImage
        src={downloadUrl}
        alt={fileName}
        fit="contain"
        className={frameClass}
        fallback={fallback}
      />
    );
  }

  if (assetType === 'video' && downloadUrl && !undecodable) {
    return (
      // `metadata` because opening a modal is asking what a file is, not asking
      // to watch it: the poster and the duration answer that, and the footage
      // stays unfetched until someone presses play. The poster is the same
      // server-rendered frame the rung below would show, so the frame carries a
      // picture of the video rather than black while the first one decodes.
      <video
        src={downloadUrl}
        controls
        preload="metadata"
        poster={thumbnailUrl ?? undefined}
        onError={() => setUndecodable(true)}
        className={`${frameClass} object-contain`}
        data-testid="file-preview-video"
      >
        <track kind="captions" />
      </video>
    );
  }

  // Audio has no picture to size, so the frame holds a player rather than
  // stretching one: a control bar scaled to a modal is not a bigger preview.
  if (fileType.startsWith('audio/') && downloadUrl && !undecodable) {
    return (
      <div className={`flex items-center justify-center ${frameClass}`}>
        <audio
          src={downloadUrl}
          controls
          preload="metadata"
          onError={() => setUndecodable(true)}
          className="w-full max-w-md"
          data-testid="file-preview-audio"
        >
          <track kind="captions" />
        </audio>
      </div>
    );
  }

  // What a PDF, a doc or a deck has: one rendered page, produced server-side.
  // Absent while extraction is pending and for types it does not cover, which
  // is the fallback's case rather than a broken image.
  if (thumbnailUrl) {
    return (
      <ThumbnailImage
        src={thumbnailUrl}
        alt={fileName}
        fit="contain"
        className={frameClass}
        fallback={fallback}
      />
    );
  }

  return <>{fallback}</>;
}
